import type { FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { PadlockIcon } from '../Icons/Miscellaneous/Padlock'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './TestScoreGauge.module.scss'

export const TestScoreGauge: FunctionComponent<TestScoreProps> = ({
  className,
  overallScore,
  textVariation,
  variant = 'small',
  padlockHandler,
}) => {
  const isDefaultVariant = variant === 'default'
  if (overallScore === undefined && !textVariation) {
    return null
  }

  if (textVariation === 'obfuscated') {
    const handleKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
      if ((event.key === 'Enter' || event.key === ' ') && padlockHandler) {
        event.preventDefault()
        padlockHandler()
      }
    }

    return (
      <div
        data-testid="test-score-gauge-obfuscated-container"
        className={classnames(
          styles.overallScoreContainer,
          styles.obfuscatedContainer,
          isDefaultVariant && styles.defaultVariant
        )}
      >
        <div
          className={styles.overallScoreInnerWrapper}
          role="img"
          aria-label="Content has been blurred out, please log in to view."
        >
          <PadlockIcon
            data-testid="badge-padlock"
            className={styles.padlockIcon}
            onClick={padlockHandler}
            onKeyDown={handleKeyDown}
            role={padlockHandler && 'button'}
            tabIndex={padlockHandler && 0}
            focusable={!!padlockHandler}
            data-which-id="padlock-signup-cta"
          />
        </div>
      </div>
    )
  }

  if (overallScore || overallScore === 0) {
    const calcDeg = 360 * (overallScore / 100)
    return (
      <div
        data-testid="test-score-value-container"
        className={classnames(
          styles.overallScoreContainer,
          isDefaultVariant && styles.defaultVariant,
          className
        )}
      >
        <div className={styles.overallScoreInnerWrapper}>
          <TypographyV2
            className={classnames(styles.overallScore)}
            {...(variant === 'default'
              ? { textStyle: 'sb-text-heading-page-title' }
              : { textStyle: 'sb-text-heading-large' })}
          >
            {overallScore}

            <TypographyV2
              tag="span"
              textStyle="sb-text-interface-body-small-strong"
              className={styles.percentage}
            >
              %
            </TypographyV2>
          </TypographyV2>
        </div>
        <div
          data-testid="overall-score-slice"
          className={classnames(
            styles.overallScoreSlice,
            overallScore > 50 && styles.overallScoreSliceActive
          )}
        >
          <div
            data-testid="overall-score-bar"
            className={styles.overallScoreBar}
            style={{
              transform: `rotate(${calcDeg}deg)`,
            }}
          ></div>
          <div
            className={styles.overallScoreFill}
            style={{
              transform: `rotate(${overallScore > 50 && calcDeg}deg)`,
            }}
          ></div>
        </div>
      </div>
    )
  }
  return (
    <TypographyV2
      data-testid="text-variation-value"
      className={classnames(
        textVariation && styles[textVariationStyle[textVariation]],
        isDefaultVariant && styles.defaultVariant
      )}
      textStyle="sb-text-body-default-strong"
    >
      {textVariation}
    </TypographyV2>
  )
}

///////// IMPLEMENTATION /////////

export type TestScoreProps = {
  /**
   * CSS class name that will be added to the TestScoreGauge component.
   */
  className?: string
  /**
   * A products test score - can be a percentage from 0-100.
   */
  overallScore?: number
  /**
   * Alternative variations of the test score.
   */
  textVariation?: 'first look' | 'no score' | 'pass' | 'fail' | 'obfuscated'
  /**
   * Decides which variant of the component is rendered
   */
  variant?: 'default' | 'small'
  /**
   * Padlock icon event handler
   */
  padlockHandler?: () => void
}

const textVariationStyle = {
  'first look': 'firstLook',
  'no score': 'noScore',
  pass: 'passOrFail',
  fail: 'passOrFail',
}
